/**
 * 메뉴 화면(출정·능력치·장비·결과)의 색 테마. 라이트가 기본이다 — 플레이한 사람이 편성 시안에서 골랐다.
 *
 * 테마는 `#screen`에만 건다. 전장 HUD(`#battle`)는 땅 그림 위에 뜨므로 늘 어두운 판이다. 색은
 * index.html의 토큰(`--ink`·`--panel-solid` …)으로만 칠하고, `#screen.light`가 그 토큰을 밝은 값으로 바꾼다.
 * 동료 합류 장면처럼 어두워야 하는 곳은 `theme-dark`로 다시 어두운 토큰을 건다.
 *
 * **자동**은 폰·PC의 밝기 설정(`prefers-color-scheme`)을 따른다. 밤에 시스템이 어두워지면 게임이 만든 어두운
 * 판으로 바뀐다. 이것을 두기 전에는 라이트를 골라도 밤이면 브라우저(삼성 인터넷·크롬 자동 어둡게)가 페이지를
 * 제멋대로 뒤집어 칠했다(플레이한 사람이 짚었다). index.html의 `color-scheme` 선언으로 막으려 했으나 삼성 인터넷은
 * 무시했다 — 브라우저의 "어두운 화면 모드"를 끄는 수밖에 없다. 끄면 브라우저가 늘 밝다고 알릴 것이라 자동도 늘
 * 밝을 것이다(폰에서 아직 확인하지 않았다). 그때 밤에 어두운 판을 원하면 "어둡게"를 직접 고른다.
 */
export type Theme = 'light' | 'dark';
/** 고른 값. `system`이면 그때그때 시스템 설정으로 `Theme`을 정한다. */
export type ThemeChoice = Theme | 'system';

const KEY = 'ildangbaek.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** 저장된 선택. 없거나 읽을 수 없으면(비공개 창 등) 라이트. */
export function savedTheme(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

/** 선택을 실제 테마로. `matchMedia`가 없는 환경(시험)에서는 자동이 라이트다. */
export function resolveTheme(choice: ThemeChoice, systemDark = prefersDark()): Theme {
  return choice === 'system' ? (systemDark ? 'dark' : 'light') : choice;
}

function prefersDark(): boolean {
  return typeof matchMedia === 'function' && matchMedia(DARK_QUERY).matches;
}

export function applyTheme(screen: HTMLElement, choice: ThemeChoice): void {
  screen.classList.toggle('light', resolveTheme(choice) === 'light');
  screen.dataset.theme = choice;
}

/** 지금 화면에 건 선택. `applyTheme`이 적어 둔다. */
export function currentChoice(screen: HTMLElement): ThemeChoice {
  const v = screen.dataset.theme;
  return v === 'dark' || v === 'system' ? v : 'light';
}

/** 밝게 → 어둡게 → 자동 → 밝게. */
export function nextChoice(choice: ThemeChoice): ThemeChoice {
  return choice === 'light' ? 'dark' : choice === 'dark' ? 'system' : 'light';
}

export const THEME_LABEL: Record<ThemeChoice, string> = { light: '밝게', dark: '어둡게', system: '자동' };

/** 다음 선택으로 바꾸고 저장한다. 새 선택을 돌려준다. */
export function cycleTheme(screen: HTMLElement): ThemeChoice {
  const next = nextChoice(currentChoice(screen));
  applyTheme(screen, next);
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // 저장이 막혀도 이번 창에서는 바뀐다.
  }
  return next;
}

/**
 * 자동일 때 시스템 설정이 바뀌면(해가 져 폰이 어두워지면) 곧바로 따라간다. 다시 그릴 것은 없다 — 테마
 * 단추의 글은 선택("자동")을 적으므로 그대로고, 칠은 `#screen.light` 하나로 바뀐다.
 */
export function watchSystemTheme(screen: HTMLElement): void {
  if (typeof matchMedia !== 'function') return;
  const mq = matchMedia(DARK_QUERY);
  const follow = (): void => {
    if (currentChoice(screen) === 'system') applyTheme(screen, 'system');
  };
  // 옛 사파리(14 전)는 addEventListener가 없고 addListener만 있다.
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', follow);
  else mq.addListener(follow);
}
